import { ConfigProvider, Empty, message, Pagination, Table, Tag, theme, Tooltip, Button, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MakeApiCall } from '../../../../../apis';
import { AddCommas, ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import { getAmount } from "../../../../../utils/helper";
import OrderDetailsModal from "../../../../../components/modal/OrderDetailsModal";

// ─── NEW: Constants ───────────────────────────────────────────────────────────
const SELLABLE   = 1;
const UNSELLABLE = 0;

// ─── NEW: Pure helpers (defined outside component) ────────────────────────────

/** Priority: local statusMap → API field → undefined */
const resolveStatus = (record, statusMap) => {
    if (statusMap[record.order_id] !== undefined) return statusMap[record.order_id];
    if (record.is_sellable_status  !== undefined) return record.is_sellable_status;
    return undefined;
};

/** A row is eligible only when refund_unit > 0 */
const isRowEligible = (record) => Number(record.refund_unit) > 0;

// ─── NEW: SellableTag sub-component ──────────────────────────────────────────
const SellableTag = ({ status }) => {
    if (status === SELLABLE)   return <Tag icon={<CheckCircleOutlined />}   color="success">Sellable</Tag>;
    if (status === UNSELLABLE) return <Tag icon={<CloseCircleOutlined />}   color="error">Unsellable</Tag>;
    return                            <Tag icon={<QuestionCircleOutlined />} color="default">Not Set</Tag>;
};

// ─── NEW: BulkActionsBar sub-component ───────────────────────────────────────
const BulkActionsBar = ({ selectedRows, onMarkSellable, onMarkUnsellable, actionLoading }) => {
    if (!selectedRows.length) return null;

    const validCount   = selectedRows.filter(isRowEligible).length;
    const invalidCount = selectedRows.length - validCount;
    const hasValid     = validCount > 0;

    return (
        <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 p-3 rounded-3"
            style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}
        >
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-bold text-primary fs-7">
                    {selectedRows.length} row{selectedRows.length > 1 ? 's' : ''} selected
                </span>
                {invalidCount > 0 && (
                    <Tag color="warning" style={{ margin: 0 }}>
                        {invalidCount} ineligible (refund = 0)
                    </Tag>
                )}
            </div>
            <Space wrap>
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    loading={actionLoading === 'sellable'}
                    disabled={!hasValid || !!actionLoading}
                    onClick={() => onMarkSellable(selectedRows)}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                    Mark as Sellable
                </Button>
                <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={actionLoading === 'unsellable'}
                    disabled={!hasValid || !!actionLoading}
                    onClick={() => onMarkUnsellable(selectedRows)}
                >
                    Mark as Unsellable
                </Button>
            </Space>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderItems = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData, isEasyShipOrder, onColumnsReady }) => {
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [totalPage, setTotalPage] = useState(0);
    const [summaryData, setSummaryData] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);

    const tableContainerRef = useRef(null);

    // Modal State
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ─── NEW: Bulk-action state ───────────────────────────────────────────────
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows]       = useState([]);
    const [actionLoading, setActionLoading]     = useState(null); // 'sellable' | 'unsellable' | null
    /** Local override map { [order_id]: 0 | 1 } — for optimistic UI updates */
    const [statusMap, setStatusMap] = useState({});

    const [filter, setFilter] = useState({
        page: 1,
        "per-page": defaultPageSize,
        marketplace_id: selectedMarketplace?.id,
        start_date: selectedTileData?.start_date,
        end_date: selectedTileData?.end_date,
        sku: selectedTileData?.sku,
        is_easy_ship_order: isEasyShipOrder,
    });

    // --- Helpers ---
    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);

    // bannerHeight is needed both for sticky offset and the observer rootMargin
    const bannerHeight = userData?.user?.user_data?.trial_expires_at ? 40 : 0;

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    // Track whether the table header is in sticky state via a 1-px sentinel
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || isMobile) return;

        const sentinel = document.createElement('div');
        sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:100%;pointer-events:none;';
        container.style.position = 'relative';
        container.prepend(sentinel);

        const observer = new IntersectionObserver(
            ([entry]) => setIsHeaderSticky(!entry.isIntersecting),
            { threshold: 0, rootMargin: `-${bannerHeight + 1}px 0px 0px 0px` }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
            sentinel.remove();
        };
    }, [bannerHeight, isMobile]);

    const getSummaryData = async (currentFilter) => {
        if (!currentFilter?.marketplace_id) return;
        try {
            const response = await MakeApiCall(
                `dashboard/tiles/get-ordered-product-analytics-data-total${ConvertParams(currentFilter)}`,
                "get",
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                setSummaryData(response.data);
            }
        } catch (error) {
            console.error("Error fetching summary data:", error);
        }
    };

    const getProductList = async (currentFilter) => {
        if (!currentFilter?.marketplace_id) {
            setList([]);
            setTotalPage(0);
            return;
        }
        setLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/tiles/get-ordered-product-analytics-data${ConvertParams(currentFilter)}`,
                "get",
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                const pagination = response?.data?.pagination || {};
                setTotalPage(pagination.totalCount || 0);
                const recordsArray = Object.values(response?.data?.records || {});
                setList(recordsArray);
                setPage(pagination.page || currentFilter.page);
                setPageSize(pagination.page_size || currentFilter["per-page"]);
                // NEW: clear row selection on every list refresh
                setSelectedRowKeys([]);
                setSelectedRows([]);
            } else {
                message.warning(response?.message);
                setList([]);
            }
        } catch (error) {
            message.error("Something went wrong while fetching data");
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    // Add a useEffect to watch for isEasyShipOrder prop changes
    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            is_easy_ship_order: isEasyShipOrder, // null, 0, or 1
            page: 1, // Reset to first page when filter changes
        }));
    }, [isEasyShipOrder]);

    useEffect(() => {
        const currentMarketplaceId = selectedMarketplace?.id;
        const currentStartDate = selectedTileData?.start_date;
        const currentEndDate = selectedTileData?.end_date;
        const currentSku = selectedTileData?.sku;

        if (
            filter.marketplace_id !== currentMarketplaceId ||
            filter.start_date !== currentStartDate ||
            filter.end_date !== currentEndDate ||
            filter.sku !== currentSku
        ) {
            setFilter(prev => ({
                ...prev,
                page: 1,
                marketplace_id: currentMarketplaceId,
                start_date: currentStartDate,
                end_date: currentEndDate,
                sku: currentSku
            }));
        }
    }, [selectedMarketplace?.id, selectedTileData?.start_date, selectedTileData?.end_date, selectedTileData?.sku]);

    useEffect(() => {
        if (filter.marketplace_id && filter.start_date && filter.end_date) {
            getProductList(filter);
            getSummaryData(filter);
        }
    }, [filter, userData?.token]);

    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    // ─── NEW: Bulk-action logic ───────────────────────────────────────────────

    const partitionRows = (rows) => ({
        validRows:   rows.filter(isRowEligible),
        invalidRows: rows.filter((r) => !isRowEligible(r)),
    });

    const callSellableStatusApi = async (rows, isSellableStatus) => {
        const { validRows, invalidRows } = partitionRows(rows);

        if (!validRows.length) {
            message.error("No eligible orders to update (all selected orders have 0 refund units).");
            return;
        }
        if (invalidRows.length > 0) {
            message.warning("Some selected orders cannot be updated due to 0 refund units.");
        }

        const actionKey = isSellableStatus === SELLABLE ? 'sellable' : 'unsellable';
        setActionLoading(actionKey);

        try {
            const amazon_order_ids = validRows.map((r) => r.order_id);
            const response = await MakeApiCall(
                "orders/sellable-status",
                "post",
                { amazon_order_ids, is_sellable_status: isSellableStatus },
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                // Optimistic UI: immediately reflect the new status in the tag
                setStatusMap((prev) => {
                    const updated = { ...prev };
                    amazon_order_ids.forEach((id) => { updated[id] = isSellableStatus; });
                    return updated;
                });
                message.success(
                    `${amazon_order_ids.length} order${amazon_order_ids.length > 1 ? 's' : ''} marked as ${isSellableStatus === SELLABLE ? 'Sellable' : 'Unsellable'} successfully.`
                );
                setSelectedRowKeys([]);
                setSelectedRows([]);
            } else {
                message.error(response?.message || "Failed to update sellable status.");
            }
        } catch (error) {
            console.error("Sellable status API error:", error);
            message.error("Something went wrong while updating sellable status.");
        } finally {
            setActionLoading(null);
        }
    };

    const handleMarkSellable   = (rows) => callSellableStatusApi(rows, SELLABLE);
    const handleMarkUnsellable = (rows) => callSellableStatusApi(rows, UNSELLABLE);

    // ─── NEW: rowSelection config ─────────────────────────────────────────────
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
        // Disable checkbox when refund_unit === 0
        getCheckboxProps: (record) => ({
            disabled: !isRowEligible(record),
            name: record.order_id,
        }),
        // Wrap disabled checkboxes in a Tooltip (antd doesn't natively do this)
        renderCell: (checked, record, index, originNode) => {
            if (isRowEligible(record)) return originNode;
            return (
                <Tooltip title="Cannot mark this order because refund unit is 0" placement="right">
                    <span>{originNode}</span>
                </Tooltip>
            );
        },
    };

    const footerCellStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '12px',
        padding: '8px 4px'
    };

    // --- Desktop Columns (original render functions preserved exactly) ---
    const allColumns = useMemo(() => [
        {
            title: "Product Details",
            key: "userDetails",
            width: 450,
            fixed: 'left',
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.image_url || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <Tooltip title={record?.product_name || ''}>
                            <span className="text-gray-800 fs-6 fw-bolder text-truncate d-inline-block" style={{ maxWidth: "300px" }}>
                                {record?.product_name || ''}
                            </span>
                        </Tooltip>
                        <div className="fs-7 mt-1">
                            <b>SKU:</b> {record?.sku} / <b>ASIN:</b> {record?.asin}
                        </div>
                        <div className="fs-7 mt-1">
                            Price: <b>{selectedCurrencySign}{AddCommas(record?.price)}</b>
                       
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Order Number",
            dataIndex: "order_id",
            key: "order_id",
            width: 250,
            render: (text, record) => (
                <div className="d-flex flex-column">
                    <Tooltip
                        title={
                            <>
                                <div><b>Order ID:</b> {record.order_id}</div>
                                <div><b>Date:</b> {new Date(record.purchase_date).toLocaleString()}</div>
                                <div><b>Status:</b> {record.order_status}</div>
                                <div><b>Channel:</b> {record?.sale_channel}</div>
                                <div><b>COGS:</b> {selectedCurrencySign}{AddCommas(record.product_cogs?.toFixed(2) || 0)}</div>
                           
                            </>
                        }
                    >
                        <span className="fw-bold text-dark cursor-pointer text-truncate d-inline-block" style={{ maxWidth: "180px" }}>
                            {record.order_id}
                        </span>
                    </Tooltip>
                    <div className="fs-7 text-gray-800">
                        {new Date(record.purchase_date).toLocaleString()} • {record.order_status}
                    </div>
                    <div className="fs-7 text-gray-800">
                        {record?.sale_channel} / COGS: <b>{selectedCurrencySign}{AddCommas(record.product_cogs?.toFixed(2) || 0)}</b>
                   
                    </div>
                </div>
            ),
        },
        { 
            title: "Units Sold", 
            dataIndex: "unit_sold", 
            key: "unit_sold", 
            width: 120, 
            render: (text) => AddCommas(text), 
            sorter: (a, b) => a.unit_sold - b.unit_sold 
        },
        { 
            title: "Refund Units", 
            dataIndex: "refund_unit", 
            key: "refund_unit", 
            width: 130, 
            render: (text) => AddCommas(text), 
            sorter: (a, b) => a.refund_unit - b.refund_unit 
        },
        { 
            title: "Sales", 
            dataIndex: "sales", 
            key: "sales", 
            width: 120, 
            render: (text) => getAmount(text, selectedCurrencySign), 
            sorter: (a, b) => a.sales - b.sales 
        },
   
        {
            title: "Amazon Fees",
            key: "amazon_fees",
            width: 140,
            render: (_, record) => {
                const totalFee = record.amazon_fees || 0;
                const details = record.more_info?.amazon_fees || {};
                const tooltipContent = (
                    <div className="p-2" style={{ minWidth: '200px' }}>
                        <div className="d-flex justify-content-between mb-1">
                            <span className="text-gray-400">FBA Fulfillment:</span>
                            <span className="fw-bold ms-4">{getAmount(details.fba_per_unit_fulfilment_fee || 0, selectedCurrencySign)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                            <span className="text-gray-400">Referral Fee:</span>
                            <span className="fw-bold ms-4">{getAmount(details.referral_fee || 0, selectedCurrencySign)}</span>
                        </div>
                    </div>
                );
                return (
                    <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                        <span className="fw-bolder text-danger cursor-pointer">
                            {getAmount(totalFee, selectedCurrencySign)}
                        </span>
                    </Tooltip>
                );
            },
            sorter: (a, b) => (a.amazon_fees || 0) - (b.amazon_fees || 0),
        },
        { title: "Gross Profit", dataIndex: "gross_profit", key: "gross_profit", width: 130, render: (text) => getAmount(text, selectedCurrencySign), sorter: (a, b) => a.gross_profit - b.gross_profit },
        // ─── Sellable Status column ───────────────────────────────────────────
        {
            title: "Sellable Status",
            key: "sellable_status",
            width: 150,
            render: (_, record) => <SellableTag status={resolveStatus(record, statusMap)} />,
        },
        {
            title: "Info",
            key: "info",
            width: 120,
            render: (_, record) => (
                <span className="cursor-pointer fw-bold  text-dark text-hover-danger" onClick={() => handleOpenModal(record)}>
                    See Details
                </span>
            ),
        },
    ], [selectedCurrencySign, statusMap]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Column visibility ────────────────────────────────────────────────────
    const HIDDEN_BY_DEFAULT = useMemo(() => new Set(['sellable_status']), []);

    const [visibleColumns, setVisibleColumns] = useState(
        () => allColumns.map(col => col.key).filter(key => !HIDDEN_BY_DEFAULT.has(key))
    );

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    const toggleableColumns = useMemo(
        () => allColumns.filter(col => col.key !== 'userDetails' && col.key !== 'info'),
        [allColumns]
    );

    const handleColumnToggle = useCallback(checkedValues => {
        setVisibleColumns(['userDetails', 'info', ...checkedValues]);
    }, []);

    // Surface column controls to parent
    useEffect(() => {
        if (typeof onColumnsReady !== 'function') return;
        onColumnsReady({ toggleableColumns, visibleColumns, handleColumnToggle });
    }, [toggleableColumns, visibleColumns, handleColumnToggle, onColumnsReady]);

    // --- Mobile Card Rendering (original structure preserved exactly) ---
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3">
            {list.map((item, index) => (
                <div
                    key={item.order_id || index}
                    className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                    style={{ background: '#fff' }}
                    onClick={() => handleOpenModal(item)}
                >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <div className="fs-9 text-uppercase text-muted fw-bold">Order Number</div>
                            <div className="fw-bolder text-primary fs-7">{item.order_id}</div>
                        </div>
                        <div className="text-end">
                            <div className="fs-9 text-muted">{new Date(item.purchase_date).toLocaleDateString()}</div>
                            <span className="badge badge-light-success fs-9 px-2 py-1">{item.order_status}</span>
                            {/* NEW: sellable status tag */}
                            <div className="mt-1"><SellableTag status={resolveStatus(item, statusMap)} /></div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center mb-4 bg-light-gray p-2 rounded-3" style={{ background: '#f8f9fa' }}>
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bold text-dark text-truncate fs-7" style={{ maxWidth: '200px' }}>{item.product_name || 'N/A'}</div>
                            <div className="text-muted fs-9">SKU: {item.sku} | ASIN: {item.asin}</div>
                        </div>
                    </div>

                    <div className="row g-3 border-top pt-3">
                        <div className="col-4 border-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Units</div>
                            <div className="fw-bolder text-dark">{item.unit_sold}</div>
                        </div>
                        <div className="col-4 border-end text-center">
                            <div className="fs-9 text-muted fw-bold mb-1">Refunds</div>
                            <div className="fw-bolder text-danger">{item.refund_unit}</div>
                        </div>
                        <div className="col-4 text-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Sales</div>
                            <div className="fw-bolder text-dark">{getAmount(item.sales, selectedCurrencySign)}</div>
                        </div>

                        <div className="col-6 mt-3">
                            <div className="fs-9 text-muted fw-bold mb-1">Amazon Fees</div>
                            <div className="fw-bolder text-danger">{getAmount(item.amazon_fees, selectedCurrencySign)}</div>
                        </div>
                        <div className="col-6 mt-3 text-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Gross Profit</div>
                            <div className="fw-bolder text-success fs-6">{getAmount(item.gross_profit, selectedCurrencySign)}</div>
                        </div>
                    </div>

                    {/* NEW: per-card action buttons, only shown when eligible (refund_unit > 0) */}
                    {isRowEligible(item) && (
                        <div className="d-flex gap-2 mt-3 border-top pt-3" onClick={(e) => e.stopPropagation()}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={actionLoading === 'sellable'}
                                disabled={!!actionLoading}
                                onClick={() => handleMarkSellable([item])}
                                style={{ background: '#52c41a', borderColor: '#52c41a', flex: 1 }}
                            >
                                Sellable
                            </Button>
                            <Button
                                size="small"
                                type="primary"
                                danger
                                icon={<CloseCircleOutlined />}
                                loading={actionLoading === 'unsellable'}
                                disabled={!!actionLoading}
                                onClick={() => handleMarkUnsellable([item])}
                                style={{ flex: 1 }}
                            >
                                Unsellable
                            </Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );


    return (
        <div>
            {/* NEW: Bulk Actions Bar — desktop only, conditionally rendered when rows are selected */}
            {!isMobile && (
                <BulkActionsBar
                    selectedRows={selectedRows}
                    onMarkSellable={handleMarkSellable}
                    onMarkUnsellable={handleMarkUnsellable}
                    actionLoading={actionLoading}
                />
            )}

            {isMobile ? (
                <div className="p-1">
                    {list.length > 0 ? renderMobileCards() : <Empty description="No Orders Found" />}
                </div>
            ) : (
                <div ref={tableContainerRef} className="shadow-sm rounded">
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        loading={loading}
                        dataSource={Array.isArray(list) ? list : []}
                        rowKey={(record) => record.order_id}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        sticky={{ offsetHeader: bannerHeight }}
                        summary={() => {
                            if (!isHeaderSticky) return null;  // hide until header sticks
                            if (!summaryData) return null;

                            const summaryDataMap = {
                                unit_sold:    summaryData.unit_sold,
                                refund_unit:  summaryData.refund_unit,
                                sales:        getAmount(summaryData.sales, selectedCurrencySign),
                                amazon_fees:  <span style={{ color: '#f1416c' }}>{getAmount(summaryData.amazon_fees, selectedCurrencySign)}</span>,
                                gross_profit: getAmount(summaryData.gross_profit, selectedCurrencySign),
                            };

                            return (
                                <Table.Summary fixed>
                                    <Table.Summary.Row className="bg-light fw-bolder">
                                        {/* checkbox column placeholder */}
                                        <Table.Summary.Cell index={0} />
                                        {columns.map((col, index) => (
                                            <Table.Summary.Cell key={col.key} index={index + 1}>
                                                {col.key === 'userDetails'
                                                    ? <div className="ps-2 fw-bold">TOTALS</div>
                                                    : <div style={footerCellStyle}>{summaryDataMap[col.key] ?? ''}</div>
                                                }
                                            </Table.Summary.Cell>
                                        ))}
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </div>
            )}

            <ConfigProvider theme={theme}>
                <div className={`d-flex ${isMobile ? 'justify-content-center' : 'justify-content-end'} mt-3`}>
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={totalPage}
                        showSizeChanger
                        simple={isMobile}
                        onChange={(currentPage, newPageSize) => {
                            setFilter(prev => ({
                                ...prev,
                                page: currentPage,
                                "per-page": newPageSize,
                            }));
                        }}
                        showTotal={(total, range) => isMobile ? `${total} Orders` : `${range[0]}-${range[1]} of ${total} items`}
                    />
                </div>
            </ConfigProvider>

            <OrderDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRecord}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default OrderItems;